import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../stores/authStore';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';

interface PresenceState {
  user_id: string;
  email: string;
  name: string;
  online_at: string;
  last_active_at: number;
  storeName: string;
  branch: string;
}

export default function PresenceToaster() {
  const { user, isAuthenticated, useDevBranch } = useAuthStore();
  const { lastActiveAt } = useIdleTimeout();
  const [activeUsers, setActiveUsers] = useState<PresenceState[]>([]);
  const channelRef = useRef<any>(null);
  
  // Para forçar o re-render local a cada 1 minuto (para atualizar o texto de "Xm inativo")
  const [, setTick] = useState(0);

  useEffect(() => {
    // Atualiza a tela a cada 1 minuto para refazer as contas de inatividade localmente
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Todos os usuários entram no mesmo canal global
    const roomName = `site-editor-presence-global`;
    const channel = supabase.channel(roomName, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channelRef.current = channel;

    // Quando o estado de presença sincroniza (alguém entra, sai ou atualiza o estado)
    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      
      const users: PresenceState[] = [];
      
      // state é um objeto onde as chaves são as presence keys (user.id definido acima)
      // e os valores são arrays de estados (presenças) daquele usuário.
      for (const id in state) {
        // Pegamos a presença mais recente (índice 0)
        const presence = state[id][0] as PresenceState;
        
        // Filtra para exibir NO TOASTER apenas usuários da mesma loja
        if (presence && presence.storeName === user.storeName) {
          users.push(presence);
        }
      }
      
      // Ordena para que o usuário logado fique primeiro, e o resto recém logado depois
      users.sort((a, b) => {
        if (a.user_id === user.id) return -1;
        if (b.user_id === user.id) return 1;
        return 0;
      });

      setActiveUsers(users);
    });

    // Inscreve no canal e envia o estado inicial
    channel.subscribe((status: any) => {
      if (status === 'SUBSCRIBED') {
        const _ = channel.track({
          user_id: user.id,
          email: user.email,
          name: user.name || user.email?.split('@')[0] || 'Usuário',
          online_at: new Date().toISOString(),
          last_active_at: Date.now(),
          storeName: user.storeName || 'unknown',
          branch: useDevBranch ? 'site-editor' : 'main',
        });
      }
    });

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
    };
  }, [isAuthenticated, user]);

  // Enviar atualizações a cada 5 minutos do last_active_at
  useEffect(() => {
    if (!channelRef.current || !user) return;

    const updatePresence = () => {
      if (channelRef.current.state === 'joined') {
        const _ = channelRef.current.track({
          user_id: user.id,
          email: user.email,
          name: user.name || user.email?.split('@')[0] || 'Usuário',
          online_at: new Date().toISOString(),
          last_active_at: lastActiveAt,
          storeName: user.storeName || 'unknown',
          branch: useDevBranch ? 'site-editor' : 'main',
        });
      }
    };

    const interval = setInterval(() => {
      updatePresence();
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(interval);
  }, [lastActiveAt, user]);

  // Se não tem ninguém online, não renderiza o wrapper
  if (activeUsers.length === 0) return null;

  const getInactiveText = (lastActive: number) => {
    const diffMs = Date.now() - lastActive;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins >= 10) {
      return ` - ${diffMins}m inativo`;
    }
    return '';
  };

  return (
    <div 
      className="fixed bottom-2 left-10 z-[2] bg-white rounded-lg shadow-lg border border-gray-200 p-1 min-w-[200px]"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="text-[8px] font-semibold text-gray-500 uppercase px-1 tracking-wider">
        Online agora
      </div>
      <div className="space-y-1">
        {activeUsers.map((u) => {
          const isMe = user?.id === u.user_id;
          const inactiveText = isMe ? getInactiveText(lastActiveAt) : getInactiveText(u.last_active_at);
          const isInactive = !!inactiveText;
          
          return (
            <div key={u.user_id} className="flex items-center space-x-2 p-1 rounded hover:bg-gray-50">
              <div className="relative flex items-center justify-center">
                <div className={`w-2 h-2 rounded-full ${isInactive ? 'bg-red-500' : 'bg-green-500'}`}></div>
                {/* Ping animation opcional apenas se ativo */}
                {!isInactive && (
                  <div className="absolute w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
                )}
              </div>
              <div className="flex flex-row">
                <span className="text-xs font-medium text-gray-700 leading-none">
                  {u.name} {isMe && <span className="text-gray-400 font-normal">(Você)</span>}
                </span>
                {isInactive && (
                  <span className="pl-1 text-[10px] text-gray-400 mt-0.5 leading-none">
                    {inactiveText.substring(3)} {/* Remove the " - " padding */}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
